import { useState, useEffect } from "react";
import { formatFileSize } from "@/utils/numberFormatter";

interface PencomFileUploadProps {
  id: string;
  label?: string;
  placeholder?: string;
  accept?: string;
  maxSize?: number; // in MB
  maxFiles?: number; // Maximum number of files allowed (default: 1 for single file, undefined for unlimited)
  onFileSelect?: (file: File | null) => void; // For backward compatibility (single file)
  onFilesSelect?: (files: File[]) => void; // For multiple files
  selectedFile?: File | null; // For backward compatibility (single file)
  selectedFiles?: File[]; // For multiple files
  className?: string;
  disabled?: boolean;
  onFileRemove?: () => void; // For backward compatibility
  onFileRemoveAt?: (index: number) => void; // For multiple files - remove at specific index
  hint?: string;
  error?: string;
  isUploading?: boolean; // Show loader when parent is uploading (e.g. to server)
  hideFileList?: boolean; // Hide the built-in selected-files list (e.g. when the parent renders its own)
  tone?: "blue" | "green"; // Visual accent for the idle dropzone state (default matches existing usages)
  showUploadButton?: boolean; // Show a standalone "Upload Files" button alongside the dropzone
}

function FileTypeIcon({ file }: { file: File }) {
  const extension = file.name.split(".").pop()?.toLowerCase() ?? "";
  const mimeType = file.type;

  if (extension === "pdf" || mimeType === "application/pdf") {
    return (
      <svg className="w-5 h-5 shrink-0" viewBox="0 0 24 24" fill="none" aria-hidden>
        <path
          d="M6 2h9l5 5v13a2 2 0 01-2 2H6a2 2 0 01-2-2V4a2 2 0 012-2z"
          fill="#FEE2E2"
          stroke="#DC2626"
          strokeWidth="1.2"
        />
        <path d="M15 2v5h5" fill="none" stroke="#DC2626" strokeWidth="1.2" />
        <text x="12" y="17" textAnchor="middle" fontSize="6" fontWeight="700" fill="#DC2626">
          PDF
        </text>
      </svg>
    );
  }

  if (mimeType.startsWith("image/")) {
    return (
      <svg className="w-5 h-5 shrink-0" viewBox="0 0 24 24" fill="none" aria-hidden>
        <rect x="2" y="4" width="20" height="16" rx="2" fill="#DBEAFE" stroke="#2563EB" strokeWidth="1.2" />
        <circle cx="8" cy="10" r="1.6" fill="#2563EB" />
        <path d="M4 17l5-5 3 3 4-4 4 4" stroke="#2563EB" strokeWidth="1.2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    );
  }

  if (
    extension === "xlsx" ||
    extension === "xls" ||
    extension === "csv" ||
    mimeType.includes("spreadsheet")
  ) {
    return (
      <svg className="w-5 h-5 shrink-0" viewBox="0 0 24 24" fill="none" aria-hidden>
        <path
          d="M6 2h9l5 5v13a2 2 0 01-2 2H6a2 2 0 01-2-2V4a2 2 0 012-2z"
          fill="#DCFCE7"
          stroke="#16A34A"
          strokeWidth="1.2"
        />
        <path d="M15 2v5h5" fill="none" stroke="#16A34A" strokeWidth="1.2" />
        <path d="M8 13h8M8 16.5h8M12 13v7" stroke="#16A34A" strokeWidth="1" />
      </svg>
    );
  }

  return (
    <img
      src="/assets/icons/document-solid.svg"
      alt=""
      className="w-4 h-4 text-primary-button flex-shrink-0"
    />
  );
}

export default function PencomFileUpload({
  id,
  label,
  placeholder = "Click here or drag and drop to upload",
  accept = ".pdf,.jpg,.jpeg,.png",
  maxSize = 10, // 10MB default
  maxFiles, // undefined = single file (backward compatible), number = max files allowed
  onFileSelect,
  onFilesSelect,
  selectedFile,
  selectedFiles,
  className = "",
  disabled = false,
  onFileRemove,
  onFileRemoveAt,
  hint,
  error: externalError,
  isUploading = false,
  hideFileList = false,
  tone = "blue",
  showUploadButton = false,
}: PencomFileUploadProps) {
  const [dragActive, setDragActive] = useState(false);
  const [internalError, setError] = useState<string>("");
  const error = externalError || internalError;

  // Determine if we're in multiple file mode
  // If maxFiles is undefined or 1, use single file mode (backward compatible)
  // If maxFiles > 1, use multiple file mode
  const isMultipleMode = maxFiles !== undefined && maxFiles > 1;
  const currentFiles = isMultipleMode
    ? selectedFiles || []
    : selectedFile
    ? [selectedFile]
    : [];

  // Reset the file input when files are cleared
  useEffect(() => {
    if (isMultipleMode) {
      if (selectedFiles && selectedFiles.length === 0) {
        const fileInput = document.getElementById(id) as HTMLInputElement;
        if (fileInput) {
          fileInput.value = "";
        }
      }
    } else {
      if (selectedFile === null) {
        const fileInput = document.getElementById(id) as HTMLInputElement;
        if (fileInput) {
          fileInput.value = "";
        }
      }
    }
  }, [selectedFile, selectedFiles, id, isMultipleMode]);

  const validateFile = (file: File, existingFiles: File[] = []): boolean => {
    // Check if we've reached the max files limit
    if (maxFiles !== undefined && existingFiles.length >= maxFiles) {
      setError(`Maximum ${maxFiles} file${maxFiles > 1 ? "s" : ""} allowed`);
      return false;
    }

    // Check for duplicate files
    const isDuplicate = existingFiles.some(
      (existingFile) =>
        existingFile.name === file.name && existingFile.size === file.size
    );
    if (isDuplicate) {
      setError(`File "${file.name}" has already been added`);
      return false;
    }

    // Check file size
    if (file.size > maxSize * 1024 * 1024) {
      setError(`File size must be less than ${maxSize}MB`);
      return false;
    }

    // Check file type
    const acceptedTypes = accept.split(",").map((type) => type.trim());
    const fileExtension = "." + file.name.split(".").pop()?.toLowerCase();
    const mimeType = file.type;

    const isValidType = acceptedTypes.some((type) => {
      if (type.startsWith(".")) {
        return fileExtension === type;
      }
      return mimeType.startsWith(type);
    });

    if (!isValidType) {
      setError(`File type not supported. Accepted types: ${accept}`);
      return false;
    }

    setError("");
    return true;
  };

  const handleFiles = (newFiles: File[]) => {
    const validFiles: File[] = [];
    const currentFileList = isMultipleMode
      ? selectedFiles || []
      : selectedFile
      ? [selectedFile]
      : [];

    for (const file of newFiles) {
      if (validateFile(file, [...currentFileList, ...validFiles])) {
        validFiles.push(file);
      }
    }

    if (validFiles.length > 0) {
      if (isMultipleMode) {
        const updatedFiles = [...currentFileList, ...validFiles];
        // Enforce maxFiles limit
        const finalFiles =
          maxFiles !== undefined
            ? updatedFiles.slice(0, maxFiles)
            : updatedFiles;
        onFilesSelect?.(finalFiles);
      } else {
        // Single file mode - take the first valid file
        onFileSelect?.(validFiles[0]);
      }
    }
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setDragActive(false);

    if (isDisabled) return;

    if (event.dataTransfer.files && event.dataTransfer.files.length > 0) {
      const filesArray = Array.from(event.dataTransfer.files);
      handleFiles(filesArray);
    }
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    if (!isDisabled) {
      setDragActive(true);
    }
  };

  const handleDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setDragActive(false);
  };

  const handleClick = () => {
    if (!disabled && !isUploading) {
      document.getElementById(id)?.click();
    }
  };

  const isDisabled = disabled || isUploading;

  const handleFileInputChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (event.target.files && event.target.files.length > 0) {
      const filesArray = Array.from(event.target.files);
      handleFiles(filesArray);
    }
  };

  const getFileSizeText = () => {
    const acceptedTypes = accept
      .split(",")
      .map((type) => type.trim().replace(".", "").toUpperCase())
      .join(", ");
    const fileLimitText =
      maxFiles !== undefined
        ? ` (max ${maxFiles} file${maxFiles > 1 ? "s" : ""}, ${maxSize}MB each)`
        : ` (max size ${maxSize}MB each)`;
    return `${acceptedTypes}${fileLimitText}`;
  };

  const handleFileRemove = (event: React.MouseEvent, index?: number) => {
    event.stopPropagation();
    setError("");

    if (isMultipleMode && index !== undefined) {
      const updatedFiles = currentFiles.filter((_, i) => i !== index);
      onFilesSelect?.(updatedFiles);
      onFileRemoveAt?.(index);
    } else {
      // Single file mode
      onFileRemove?.();
      onFileSelect?.(null);
    }

    // Reset the file input value to allow re-uploading the same file
    const fileInput = document.getElementById(id) as HTMLInputElement;
    if (fileInput) {
      fileInput.value = "";
    }
  };

  return (
    <div className="w-full ">
      {label && (
        <label
          htmlFor={id}
          className="font-clash-display text-medium-dark-grey text-md font-semibold"
        >
          {label}
        </label>
      )}

      <div className="relative w-full">
        {isUploading && (
          <div
            className="absolute inset-0 z-10 flex flex-col items-center justify-center rounded-lg bg-white/80 border-2 border-dashed border-primary-button/30"
            aria-busy="true"
            aria-label="Uploading"
          >
            <div className="w-10 h-10 border-2 border-primary-button border-t-transparent rounded-full animate-spin" />
            <p className="font-clash-display text-sm font-medium text-primary-button mt-3">
              Uploading...
            </p>
          </div>
        )}
        <div
          className={`
          w-full flex flex-col items-center justify-center p-10 border-2 border-dashed rounded-lg text-center relative
          ${
            error
              ? "border-red-500 bg-red-50"
              : dragActive
              ? "border-primary-button bg-primary-button/5"
              : tone === "green"
              ? "border-primary-button/40 bg-pale-mint-green"
              : "border-navy-blue bg-baby-blue"
          }
          ${
            isDisabled
              ? "opacity-50 cursor-not-allowed"
              : "cursor-pointer hover:border-primary-button hover:bg-primary-button/5"
          }
          ${className}
        `}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onClick={handleClick}
        >
          {tone === "green" ? (
            <svg
              className="w-8 h-8 text-primary-button"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden
            >
              <path
                d="M12 16V4M12 4l5 5M12 4L7 9"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          ) : (
            <img
              src="/assets/icons/document-solid.svg"
              alt="upload icon"
              className="w-8 h-8 text-primary-button"
            />
          )}
          <p className="font-clash-display text-sm font-medium text-medium-dark-grey">
            {placeholder}
          </p>
          <p className="font-clash-display text-xs font-regular text-medium-dark-grey">
            {getFileSizeText()}
          </p>
          {hint && (
            <p className="font-clash-display text-xs font-regular text-medium-dark-grey mt-1">
              {hint}
            </p>
          )}

          {!hideFileList && currentFiles.length > 0 && (
            <div className="flex flex-col gap-2 mt-3 w-full">
              {currentFiles.map((file, index) => (
                <div
                  key={`${file.name}-${index}`}
                  className="flex items-center justify-between bg-primary-button/10 border border-primary-button/20 rounded-lg p-3 w-full"
                >
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    <FileTypeIcon file={file} />
                    <p className="font-clash-display text-xs text-primary-button font-medium truncate">
                      {file.name}
                    </p>
                    <p className="font-clash-display text-xs text-primary-button/70 shrink-0">
                      {formatFileSize(file.size)}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={(e) => handleFileRemove(e, index)}
                    disabled={isDisabled}
                    className="flex items-center justify-center w-6 h-6 rounded-full bg-red-500 hover:bg-red-600 text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0 ml-2"
                    title="Remove file"
                  >
                    <svg
                      className="w-3 h-3"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                </div>
              ))}
            </div>
          )}

          {error && (
            <p className="font-clash-display text-xs text-red-500 mt-2">
              {error}
            </p>
          )}

          <input
            type="file"
            id={id}
            accept={accept}
            className="hidden"
            onChange={handleFileInputChange}
            disabled={isDisabled}
            multiple={isMultipleMode}
          />
        </div>

        {showUploadButton && (
          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              handleClick();
            }}
            disabled={isDisabled}
            className="absolute right-4 top-1/2 -translate-y-1/2 flex items-center gap-x-2 rounded-lg border border-primary-button/30 bg-white px-4 py-2 font-clash-display text-sm font-semibold text-primary-button hover:bg-primary-button/5 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" aria-hidden>
              <path
                d="M12 16V4M12 4l5 5M12 4L7 9"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            Upload Files
          </button>
        )}
      </div>
    </div>
  );
}
